import React, { useEffect, useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, Hidden, Tab, Theme, useMediaQuery } from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useVideo } from 'hooks';
import { VideoPreviewWithControls } from 'components';
import { EditWaitingRoom } from '../EditWaitingRoom';

import styles from './EditWaitingRoomContainer.module.scss';

type EditWaitingRoomContainerProps = {
  onDirtyChange?(dirty: boolean): void;
};

export function EditWaitingRoomContainer({ onDirtyChange }: EditWaitingRoomContainerProps) {
  const { t } = useTranslation('providerRoom');
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { getAudioAndVideoTracks } = useVideo();
  const [isEditWaitingRoomDirty, setIsEditWaitingRoomDirty] = React.useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === '/' && isEditWaitingRoomDirty && !window.confirm(t('unsavedChangesPrompt'))) {
      return;
    }

    setIsEditWaitingRoomDirty(false);
    navigate(newValue);
  };

  useEffect(() => {
    getAudioAndVideoTracks();
  }, []);

  const cardHeaderTitle = useMemo(() => {
    let title: React.ReactNode;
    switch (true) {
      case isDesktop: {
        title = (
          <TabList onChange={handleChange} aria-label={t('waitingRoomTabsAria')}>
            <Tab label={t('myRoomTab')} value='/' disableRipple disableTouchRipple />
            <Tab label={t('editWaitingRoomTab')} value='/edit' disableRipple disableTouchRipple />
          </TabList>
        );
        break;
      }
      default: {
        title = t('editWaitingRoomTab');
        break;
      }
    }
    return title;
  }, [isDesktop, t]);

  return (
    <>
      <TabContext value={'/edit'}>
        <Card
          className={styles.main}
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined,
          })}
        >
          <CardHeader title={cardHeaderTitle} sx={{ padding: isDesktop ? 0 : '0 20px' }} />
          <CardContent className={styles.content}>
            <Box
              className={styles.contentWrapper}
              maxWidth={isDesktop ? 510 : '100%'}
              sx={(theme) => ({
                color: theme.palette.text.primary,
              })}
            >
              <EditWaitingRoom
                onDirtyChange={(dirty) => {
                  setIsEditWaitingRoomDirty(dirty);
                  onDirtyChange?.(dirty);
                }}
              />
            </Box>
            <Hidden only={['sm', 'xs']}>
              <Box className={styles.preview}>
                <VideoPreviewWithControls />
              </Box>
            </Hidden>
          </CardContent>
        </Card>
      </TabContext>
    </>
  );
}
